import { BSC_CHAIN_ID_NUMBER, ETH_CHAIN_ID_NUMBER, MATIC_CHAIN_ID_NUMBER } from '../constants/networks'
import { Network } from '../interfaces'

export enum ChainId {
  BSC_MAINNET = 56,
  BSC_TESTNET = 97,
  MATIC_MAINNET = 137,
  MATIC_TESTNET = 80002,
  ETH_MAINNET = 1,
  ETH_GOERLI = 5,
  ETH_SEPOLIA = 11155111,
  ARBITRUM_MAINNET = 42161,
  ARBITRUM_TESTNET = 421614,
  BASE_MAINNET = 8453,
  BASE_TESTNET = 84532
}

interface GetChainScanLinkProps {
  data: string
  type: 'transaction' | 'token' | 'address'
  network?: Network
  chainId?: ChainId
}

const SCAN_PREFIXES: { [chainId in ChainId]: string } = {
  56: 'bscscan.com',
  97: 'testnet.bscscan.com',
  137: 'polygonscan.com',
  80002: 'mumbai.polygonscan.com',
  1: 'etherscan.io',
  5: 'goerli.etherscan.io',
  11155111: 'sepolia.etherscan.io',
  42161: 'arbiscan.io',
  421614: 'sepolia.arbiscan.io',
  8453: 'basescan.org',
  84532: 'sepolia.basescan.org'
}

export const getScanNameByChainId = (chainId?: ChainId): string | undefined => {
  switch (chainId) {
    case ChainId.BSC_MAINNET:
      return 'BSCScan'
    case ChainId.BSC_TESTNET:
      return 'BSCScan Testnet'
    case ChainId.MATIC_MAINNET:
      return 'PolygonScan'
    case ChainId.MATIC_TESTNET:
      return 'Mumbai PolygonScan'
    case ChainId.ETH_MAINNET:
      return 'Etherscan'
    case ChainId.ETH_GOERLI:
      return 'Goerli Etherscan'
    case ChainId.ETH_SEPOLIA:
      return 'Sepolia Etherscan'
    case ChainId.ARBITRUM_MAINNET:
      return 'Arbiscan'
    case ChainId.ARBITRUM_TESTNET:
      return 'Arbitrum Sepolia Testnet'
    case ChainId.BASE_MAINNET:
      return 'Base Scan'
    case ChainId.BASE_TESTNET:
      return 'Base Scan Testnet'
    default:
      return undefined
  }
}

export const getChainIdByNetwork = (network: Network): ChainId => {
  switch (network) {
    case Network.BNB_CHAIN:
      return Number.isNaN(BSC_CHAIN_ID_NUMBER) ? ChainId.BSC_MAINNET : (BSC_CHAIN_ID_NUMBER as ChainId)
    case Network.POLYGON:
      return Number.isNaN(MATIC_CHAIN_ID_NUMBER) ? ChainId.MATIC_MAINNET : (MATIC_CHAIN_ID_NUMBER as ChainId)
    case Network.ETHEREUM:
      return ETH_CHAIN_ID_NUMBER
    case Network.ARBITRUM:
      return ChainId.ARBITRUM_MAINNET
    case Network.BASE:
      return ChainId.BASE_MAINNET
    default:
      return ETH_CHAIN_ID_NUMBER
  }
}

export function getChainScanLink(props: GetChainScanLinkProps): string {
  const { data, type } = props
  let { chainId = ETH_CHAIN_ID_NUMBER as ChainId } = props
  if (props?.network) {
    chainId = getChainIdByNetwork(props?.network)
  }
  const scanHost = SCAN_PREFIXES[chainId] || SCAN_PREFIXES[ChainId.ETH_MAINNET]
  const prefix = `https://${scanHost}`

  switch (type) {
    case 'transaction': {
      return `${prefix}/tx/${data}`
    }
    case 'token': {
      return `${prefix}/token/${data}`
    }
    case 'address':
    default: {
      return `${prefix}/address/${data}`
    }
  }
}
